/**
 * Landing Page ViewModel
 * Contains ALL business logic and state management for the landing feature
 * Following MVVM pattern - View should be purely presentational
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLoginMutation, useGetSteConfigQuery } from "../../../api";
import { Analytics } from "../../../services/analytics";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { setCredentials, selectIsAuthenticated, logout } from "../../auth";
import { Routes } from "../../../routes";
import { detectPlatform, type Platform } from "../../../utils/platform";
import type {
  AppBenefit,
  AppStoreConfig,
  LandingConfig,
} from "../model/LandingModel";
import {
  defaultBenefits,
  defaultAppStoreConfig,
  defaultLandingConfig,
} from "../model/LandingModel";

// View State - what the View needs to render
export interface LandingViewState {
  config: LandingConfig;
  appStoreConfig: AppStoreConfig;
  benefits: AppBenefit[];
  isLoading: boolean;
  isConfigLoading: boolean;
  error: string | null;
  hasSteToken: boolean;
  platform: Platform;
}

// View Actions - callbacks from View to ViewModel
export interface LandingViewActions {
  handlePlayStoreClick: () => void;
  handleAppStoreClick: () => void;
  handleLoginClick: () => Promise<void>;
  clearError: () => void;
}

export interface LandingViewModel
  extends LandingViewState, LandingViewActions {}

interface UseLandingViewModelOptions {
  config?: Partial<LandingConfig>;
  appStoreConfig?: Partial<AppStoreConfig>;
  benefits?: AppBenefit[];
}

/**
 * Custom hook that provides the ViewModel for the Landing page
 * All business logic is encapsulated here
 */
export function useLandingViewModel(
  options: UseLandingViewModelOptions = {},
): LandingViewModel {
  const {
    config: customConfig,
    appStoreConfig: customAppStoreConfig,
    benefits: customBenefits,
  } = options;

  // Navigation & Redux
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  // URL params
  const [searchParams] = useSearchParams();
  // Get token and fix base64 encoding: + characters in URLs are decoded as spaces
  // We need to replace spaces back to + for proper base64 token
  const rawToken = searchParams.get("ste");
  const steToken = rawToken ? rawToken.replace(/ /g, "+") : null;

  // API
  // Only fetch config AFTER user is authenticated
  const { data: steConfig, isLoading: isConfigLoading } = useGetSteConfigQuery(
    undefined,
    { skip: !isAuthenticated },
  );

  const [login, { isLoading: isLoginLoading }] = useLoginMutation();

  // Local state
  const [error, setError] = useState<string | null>(null);

  // Auto-append ?ste=token & Trigger Background Login
  useEffect(() => {
    if (steToken && !isAuthenticated) {
      // Ensure URL has the token
      const currentParams = new URLSearchParams(window.location.search);
      if (!currentParams.get("ste")) {
        const encodedToken = encodeURIComponent(steToken);
        navigate(`${Routes.LANDING}?ste=${encodedToken}`, { replace: true });
      }

      // Trigger background login
      if (!isLoginLoading && !error) {
        login({ token: steToken })
          .unwrap()
          .then((result) => {
            dispatch(
              setCredentials({
                token: result.token,
                steContext: result.steContext,
              }),
            );
            Analytics.track("login_success_background");
          })
          .catch((err) => {
            console.error("Background login failed:", err);
            Analytics.track("login_failed_background", { error: String(err) });
            setError("Login failed. Please try again.");
          });
      }
    }
  }, [
    steToken,
    isAuthenticated,
    navigate,
    login,
    dispatch,
    isLoginLoading,
    error,
  ]);

  // Merge custom config with defaults and API config
  const config = useMemo<LandingConfig>(
    () => ({
      ...defaultLandingConfig,
      ...customConfig,
      ...(steConfig?.web || {}),
    }),
    [customConfig, steConfig],
  );

  const appStoreConfig = useMemo<AppStoreConfig>(
    () => ({
      ...defaultAppStoreConfig,
      ...customAppStoreConfig,
    }),
    [customAppStoreConfig],
  );

  const benefits = useMemo<AppBenefit[]>(
    () => customBenefits ?? defaultBenefits,
    [customBenefits],
  );

  // Detect platform
  const platform = useMemo(() => detectPlatform(), []);

  // Handle Play Store click
  const handlePlayStoreClick = useCallback(() => {
    dispatch(logout());
    window.open(appStoreConfig.playStoreUrl, "_blank");
  }, [appStoreConfig.playStoreUrl, dispatch]);

  // Handle App Store click
  const handleAppStoreClick = useCallback(() => {
    dispatch(logout());
    window.open(appStoreConfig.appStoreUrl, "_blank");
  }, [appStoreConfig.appStoreUrl, dispatch]);

  // Handle "Continue in Browser" click - contains all login logic
  const handleLoginClick = useCallback(async () => {
    // If already authenticated (e.g. background login success), just navigate
    if (isAuthenticated) {
      navigate(Routes.HOME);
      return;
    }

    // No token - show error message
    if (!steToken) {
      setError("No QR token found. Please scan a QR code to access the app.");
      return;
    }

    try {
      setError(null);

      // Call login API with QR token
      const result = await login({ token: steToken }).unwrap();

      // Store credentials in Redux
      dispatch(
        setCredentials({
          token: result.token,
          steContext: result.steContext,
        }),
      );

      Analytics.track("login_success");
      // Navigate to home page on success
      navigate(Routes.HOME);
    } catch (err) {
      console.error("Login failed:", err);
      Analytics.track("login_failed", { error: String(err) });
      setError("Login failed. Please try again or scan a new QR code.");
    }
  }, [steToken, login, dispatch, navigate, isAuthenticated]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // If there's a token and we haven't failed, we consider it "loading" only if we haven't authenticated yet
  // Once authenticated via background login, we stop "loading" so the user can click the button
  const isBackgroundLoginPending = !!steToken && !isAuthenticated && !error;

  return {
    // State
    config,
    appStoreConfig,
    benefits,
    isLoading: isLoginLoading || isBackgroundLoginPending,
    isConfigLoading: isConfigLoading || (isAuthenticated && !steConfig), // Consider it loading till we actually have the config after login
    error,
    hasSteToken: !!steToken,
    platform,

    // Actions
    handlePlayStoreClick,
    handleAppStoreClick,
    handleLoginClick,
    clearError,
  };
}
